import { useEffect, useMemo, useState } from 'react';
import styles from './sidebar.module.scss';
import { SidebarUsersProps } from '../../../../interfaces/components';
import { useToast } from '../../../../helpers/hooks';
import { capitalize, formatRut, validateRut } from '../../../../helpers/formatters';
import ButtonShared from '../../../../components/Button/Button';
import SidebarShared from '../../../../components/Sidebar/Sidebar';
import SelectShared from '../../../../components/Select/Select';
import TextFieldShared from '../../../../components/TextField/TextField';
import PhoneInput from '../../../../components/PhoneInput/PhoneInput';
import { CIVIL_STATUSES, GENDER_STATUSES } from '../../../../helpers/constants';
import DatePickerShared from '../../../../components/DatePicker/DatePicker';
import moment from 'moment';
import { profileTranslations } from '../../../../constants/translactions';

const Sidebar = ({
    user,
    state,
    setState,
    userSelected,
    addPickupPoint,
    cleanAddPickupPoint,
    pickupPoints,
    editPickupPoint,
    cleanEditPickupPoint,
    getDealerships, 
    dealerships,
    cleanGetDealerships,
    getBranchs,
    branchs,
    cleanGetBranchs,
    getProfiles,
    profiles,
    cleanGetProfiles,
    getRegions,
    regions,
    cleanGetRegions,
    getCities,
    cities,
    cleanGetCities,
    getCommunes,
    communes,
    cleanGetCommunes
}: any) => {
    const toast = useToast();
    const [name, setName] = useState<any>({value: "", error: ""});
    const [address, setAddress] = useState<any>({value: "", error: ""});
    const [region, setRegion] = useState<any>({value: "", error: ""});
    const [city, setCity] = useState<any>({value: "", error: ""});
    const [commune, setCommune] = useState<any>({value: "", error: ""});
    const [lastName, setLastName] = useState<any>({value: "", error: ""});
    const [rut, setRut] = useState<any>({value: "", error: ""});
    const [email, setEmail] = useState<any>({value: "", error: ""});
    const [password, setPassword] = useState<any>({
        value: "", 
        error: "", 
        validation: {
            specialCaracter: false,
            mayus: false,
            correctLength: false,
            number: false
        }
    });
    const [phoneValue, setPhoneValue] = useState<any>({code: "", number: "", error: ""});
    const [birthday, setBirthday] = useState<any>({value: "", error: ""});
    const [civilSelected, setCivilSelected] = useState<any>({value: "", error: ""});
    const [genderSelected, setGenderSelected] = useState<any>({value: "", error: ""});
    const [profileSelected, setProfileSelected] = useState<any>({value: "", error: ""});
    const [dealershipSelected, setDealershipSelected] = useState<any>({value: "", error: ""});
    const [branchSelected, setBranchSelected] = useState<any>({value: "", error: ""});

    const handleChangePhone = (value: any, type: string) => {
        if(type === "code"){
            setPhoneValue({...phoneValue, code: value});
        }else{
            setPhoneValue({...phoneValue, number: value});
        }
    }

    const onSubmit = () => {

        if(!name?.value) {return setName({...name, error: "Debes ingresar un nombre"})} else setName({...name, error: ""});
        
        let bodyRequest: any = {
            "name": name?.value,
        };

        if(address?.value) {
            bodyRequest.address = address?.value;
        }

        if(region?.value || city?.value || commune?.value) {
            if(region?.value) bodyRequest.region = region?.value;
            if(city?.value) bodyRequest.city = city?.value;
            if(commune?.value) bodyRequest.commune = commune?.value;
        }

        if(address?.value?.coordinates) {
            bodyRequest.coordinates = {
                lat: address?.value?.coordinates?.lat || 0,
                long: address?.value?.coordinates?.lng || 0
            };
        }

        if(state.type === "add") addPickupPoint(bodyRequest);
        if(state.type === "edit") editPickupPoint(userSelected?._id, bodyRequest);
    }

    const handleChangeDealership = (value: string) => {
        setDealershipSelected({...dealershipSelected, value: value});
        if(profileSelected?.value === "admin-branch" || profileSelected?.value === "seller"){
            getBranchs(value);
        }
    }

    const handleChangeRut = (value: any) => {
        const formatValue = formatRut(value);
        if(!validateRut(formatValue)){
            return setRut({value: formatValue, error: "Ingresa un RUT válido"});
        }
        setRut({value: formatValue, error: ""});
    }

    const civilNormalized: any = useMemo(() => {
        return CIVIL_STATUSES && CIVIL_STATUSES.map((item: any, index: number) => (
            {
                id: item?.id,
                name: item?.label
            }
        ))
    }, [CIVIL_STATUSES]);

    const genderNormalized: any = useMemo(() => {
        return GENDER_STATUSES && GENDER_STATUSES.map((item: any, index: number) => (
            {
                id: index,
                name: item
            }
        ))
    }, [GENDER_STATUSES]);

    const profilesNormalized: any = useMemo(() => {
        return profiles?.data && profiles?.data
            .filter((item: any) => {
                const lowercaseName = item?.name?.toLowerCase();
                return lowercaseName !== "admin" && lowercaseName !== "viewer admin" && lowercaseName !== "viewer-admin";
            })
            .map((item: any) => (
                {
                    id: item?.code,
                    name: profileTranslations(item?.name)
                }
            ))
    }, [profiles?.data]);

    const dealershipsNormalized: any = useMemo(() => {
        return dealerships?.data && dealerships?.data.map((item: any) => (
            {
                id: item?._id,
                name: item?.name,
                address: item?.address
            }
        ))
    }, [dealerships?.data]);

    const branchsNormalized: any = useMemo(() => {
        return branchs?.data && branchs?.data.map((item: any) => (
            {
                id: item?._id,
                name: item?.name
            }
        ))
    }, [branchs?.data]);

    const regionsNormalized: any = useMemo(() => {
        const regionsArray = regions?.data?.company || [];
        const normalized = Array.isArray(regionsArray) ? regionsArray.map((item: any) => (
            {
                id: item?.region_id,
                name: item?.name,
                regionName: item?.name
            }
        )) : [];
        return normalized;
    }, [regions?.data]);

    const citiesNormalized: any = useMemo(() => {
        const citiesArray = cities?.data || [];
        const normalized = Array.isArray(citiesArray) ? citiesArray.map((item: any) => (
            {
                id: item?.city_id,
                name: item?.name,
                cityName: item?.name
            }
        )) : [];
        return normalized;
    }, [cities?.data]);

    const communesNormalized: any = useMemo(() => {
        const communesArray = communes?.data || [];
        const normalized = Array.isArray(communesArray) ? communesArray.map((item: any) => (
            {
                id: item?.name,
                name: item?.name
            }
        )) : [];
        return normalized;
    }, [communes?.data]);

    const showDealerships = useMemo(() => {
        return (profileSelected?.value === "admin-dealership" || profileSelected?.value === "admin-branch" || profileSelected?.value === "seller") ? true : false;
    }, [profileSelected?.value]);

    const showBranchs = useMemo(() => {
        return (profileSelected?.value === "admin-branch" || profileSelected?.value === "seller") ? true : false;
    }, [profileSelected?.value]);

    useEffect(() => {
        if(branchs?.fetching === "failure"){
            toast("Ha ocurrido un error al traer las sucursales");
            cleanGetBranchs();
        }
        if(branchs?.fetching === "success"){
            cleanGetBranchs();
        }
    }, [branchs?.fetching]);

    useEffect(() => {
        if(dealerships?.fetching === "failure"){
            toast("Ha ocurrido un error al traer las concesionarias");
            cleanGetDealerships();
        }
        if(dealerships?.fetching === "success"){
            cleanGetDealerships();
        }
    }, [dealerships?.fetching]);

    useEffect(() => {
        if(profiles?.fetching === "failure"){
            toast("Ha ocurrido un error al traer los perfiles");
            cleanGetProfiles();
        }
        if(profiles?.fetching === "success"){
            cleanGetProfiles();
        }
    }, [profiles?.fetching]);

    useEffect(() => {
        if(regions?.fetching === "failure"){
            toast("Ha ocurrido un error al traer las regiones");
            cleanGetRegions();
        }
    }, [regions?.fetching]);

    useEffect(() => {
        if(cities?.fetching === "failure"){
            toast("Ha ocurrido un error al traer las ciudades");
            cleanGetCities();
        }
    }, [cities?.fetching]);

    useEffect(() => {
        if(communes?.fetching === "failure"){
            toast("Ha ocurrido un error al traer las comunas");
            cleanGetCommunes();
        }
    }, [communes?.fetching]);

    const handleChangeRegion = (value: any) => {
        const selectedRegion = regionsNormalized.find((r: any) => r.id === value);
        setRegion({value: selectedRegion?.regionName || "", error: ""});
        setCity({value: "", error: ""});
        setCommune({value: "", error: ""});
        cleanGetCities();
        cleanGetCommunes();
        if(value) {
            getCities(value);
        }
    };

    const handleChangeCity = (value: any) => {
        const selectedCity = citiesNormalized.find((c: any) => c.id === value);
        setCity({value: selectedCity?.cityName || "", error: ""});
        setCommune({value: "", error: ""});
        cleanGetCommunes();
        if(value) {
            getCommunes(value);
        }
    };

    useEffect(() => {
        if(state.open){
            getProfiles();
            getRegions();
            if(userSelected?._id){
                setName({value: userSelected?.name || "", error: ""});
                setAddress({value: userSelected?.address || "", error: ""});
                setRegion({value: userSelected?.region || "", error: ""});
                setCity({value: userSelected?.city || "", error: ""});
                setCommune({value: userSelected?.commune || "", error: ""});
                setLastName({value: userSelected?.last_name, error: ""});
                setRut({value: formatRut(userSelected?.legal_id), error: ""});
                setPhoneValue({code: userSelected?.phone?.code, number: userSelected?.phone?.number, error: ""});
                setEmail({value: userSelected?.email, error: ""});
                setPassword({
                    value: "", 
                    error: "", 
                    validation: {
                        specialCaracter: false,
                        mayus: false,
                        correctLength: false,
                        number: false
                    }
                });
                setBirthday({value: userSelected?.date_of_birth, error: ""});
                setCivilSelected({value: userSelected?.civil_status, error: ""});
                setGenderSelected({value: userSelected?.gender, error: ""});
                setProfileSelected({value: userSelected?.profile?.code ?? "", error: ""});
            }else{
                cleanGetCities();
                cleanGetCommunes();
                setName({value: "", error: ""});
                setAddress({value: "", error: ""});
                setRegion({value: "", error: ""});
                setCity({value: "", error: ""});
                setCommune({value: "", error: ""});
                setLastName({value: "", error: ""});
                setRut({value: "", error: ""});
                setPhoneValue({code: "", number: "", error: ""});
                setEmail({value: "", error: ""});
                setPassword({
                    value: "", 
                    error: "", 
                    validation: {
                        specialCaracter: false,
                        mayus: false,
                        correctLength: false,
                        number: false
                    }
                });
                setBirthday({value: "", error: ""});
                setCivilSelected({value: "", error: ""});
                setGenderSelected({value: "", error: ""});
                setProfileSelected({value: "", error: ""});
            }
        }else{
            cleanGetCities();
            cleanGetCommunes();
            setName({value: "", error: ""});
            setAddress({value: "", error: ""});
            setRegion({value: "", error: ""});
            setCity({value: "", error: ""});
            setCommune({value: "", error: ""});
            setLastName({value: "", error: ""});
            setRut({value: "", error: ""});
            setPhoneValue({code: "", number: "", error: ""});
            setEmail({value: "", error: ""});
            setPassword({
                value: "", 
                error: "", 
                validation: {
                    specialCaracter: false,
                    mayus: false,
                    correctLength: false,
                    number: false
                }
            });
            setBirthday({value: "", error: ""});
            setCivilSelected({value: "", error: ""});
            setGenderSelected({value: "", error: ""});
            setProfileSelected({value: "", error: ""});
        }
    }, [state.open]);

    useEffect(() => {
        if(regions?.fetching === "success" && userSelected?._id && userSelected?.region){
            const selectedRegion = regions?.data?.company?.find((r: any) => r.name === userSelected?.region);
            if(selectedRegion?.region_id){
                getCities(selectedRegion.region_id);
            }
        }
    }, [regions?.fetching]);

    useEffect(() => {
        if(cities?.fetching === "success" && userSelected?._id && userSelected?.city){
            const selectedCity = cities?.data?.find((c: any) => c.name === userSelected?.city);
            if(selectedCity?.city_id){
                getCommunes(selectedCity.city_id);
            }
        }
    }, [cities?.fetching]);

    useEffect(() => {
        if(pickupPoints?.add?.fetching === "failure"){
            cleanAddPickupPoint();
            switch(pickupPoints?.add?.error){
                default:
                    toast("Ha ocurrido un error al añadir un nuevo punto de inspección");
                    break;
            }
        }
        if(pickupPoints?.add?.fetching === "success"){
            toast("Punto de inspección añadido correctamente");
            setState({...state, open: false});
            cleanAddPickupPoint();
        }
    }, [pickupPoints?.add?.fetching]);

    useEffect(() => {
        if(pickupPoints?.edit?.fetching === "failure"){
            toast("Ha ocurrido un error al editar el usuario");
            cleanEditPickupPoint();
        }
        if(pickupPoints?.edit?.fetching === "success"){
            toast("Punto de inspección editado correctamente");
            setState({...state, open: false});
            cleanEditPickupPoint();
        }
    }, [pickupPoints?.edit?.fetching]);

    useEffect(() => {
        setState(state);
    }, [state]);

    return (
        <SidebarShared 
            variant="close" 
            open={state.open} 
            title={`${state.type === "add" ? "Nuevo" : state.type === "edit" ? "Editar": "Eliminar"} punto de inspeccion`} 
            onBack={() => setState({...state, open: false})} 
            id="usersSidebarContent"
        >
            <div className={styles.sidebarContent}>
                <div className={styles.inputs}>
                    <TextFieldShared
                        label="Nombre"
                        name="name"
                        value={name?.value}
                        onChange={(value: any) => setName({...name, value: value})}
                        error={name?.error}
                        className={`${styles.name} ${styles.full}`}
                    />
                    <TextFieldShared
                        variant="location"
                        label="Dirección"
                        name="address"
                        value={address?.value}
                        onChange={({ value }: any) => setAddress({ ...address, value: value })}
                        error={address?.error}
                        className={`${styles.full}`}
                    />
                    <SelectShared
                        label="Región"
                        name="region"
                        variant="filled"
                        value={regionsNormalized.find((r: any) => r.regionName === region?.value)?.id || ""}
                        onChange={handleChangeRegion}
                        error={region?.error}
                        className={`${styles.full}`}
                        options={regionsNormalized}
                    />
                    <SelectShared
                        label="Ciudad"
                        name="city"
                        variant="filled"
                        value={citiesNormalized.find((c: any) => c.cityName === city?.value)?.id || ""}
                        onChange={handleChangeCity}
                        error={city?.error}
                        className={`${styles.full}`}
                        options={citiesNormalized}
                        disabled={!region?.value}
                        loading={cities?.fetching === "loading"}
                    />
                    <SelectShared
                        label="Comuna"
                        name="commune"
                        variant="filled"
                        value={commune?.value}
                        onChange={(value: any) => setCommune({...commune, value: value})}
                        error={commune?.error}
                        className={`${styles.full}`}
                        options={communesNormalized}
                        disabled={!city?.value}
                        loading={communes?.fetching === "loading"}
                    />
                </div>
                <ButtonShared
                    onPress={onSubmit}
                    title={
                        state.type === "add" 
                        ? "Crear punto de inspección" 
                        : "Guardar cambios"
                    }
                    className={styles.sendInvitation}
                    primary
                    loading={pickupPoints?.add?.fetching === "loading" || pickupPoints?.edit?.fetching === "loading"}
                />
            </div>
        </SidebarShared>
    )
};
export default Sidebar;